import React, { useEffect, useState } from "react";
import { getSectionSponsors } from "../lib/api";

// Module-level cache so many badges share one request (resets on full reload).
let _cache;
const load = () => {
  if (!_cache) _cache = getSectionSponsors().catch(() => []);
  return _cache;
};

export default function SectionSponsor({ section, className = "", variant = "chip" }) {
  const [sponsor, setSponsor] = useState(null);
  useEffect(() => {
    load().then((list) => setSponsor((list || []).find((x) => x.section === section && x.active !== false) || null));
  }, [section]);

  if (!sponsor) return null;

  if (variant === "portal") {
    const cls = `group flex shrink-0 items-center gap-3 rounded-2xl bg-white px-3 py-2 shadow-lg sm:px-4 sm:py-2.5 ${className}`;
    const content = (
      <>
        <div className="flex flex-col items-start">
          <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-slate-400">Sponsored by</span>
          {sponsor.logo_url
            ? <img src={sponsor.logo_url} alt={sponsor.sponsor_name} className="h-9 max-w-[130px] object-contain sm:h-14 sm:max-w-[190px]" />
            : <span className="text-lg font-extrabold text-slate-800">{sponsor.sponsor_name}</span>}
        </div>
        {sponsor.offer && (
          <span data-testid={`section-sponsor-offer-${section}`} className="block max-w-[80px] border-l border-slate-200 pl-2 text-[11px] font-extrabold leading-tight text-[#3E8B3A] group-hover:underline sm:max-w-[130px] sm:pl-3 sm:text-sm">
            {sponsor.offer}
          </span>
        )}
      </>
    );
    const href = sponsor.offer_link || sponsor.link;
    return href
      ? <a href={href} target="_blank" rel="noreferrer" data-testid={`section-sponsor-${section}`} className={`${cls} transition-transform hover:-translate-y-0.5`}>{content}</a>
      : <span data-testid={`section-sponsor-${section}`} className={cls}>{content}</span>;
  }

  if (variant === "masthead") {
    const content = (
      <>
        <div className="flex flex-col items-end text-right">
          <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">Sponsored by</span>
          {sponsor.message && <span className="max-w-[190px] text-[11px] leading-tight text-slate-500">{sponsor.message}</span>}
        </div>
        {sponsor.logo_url
          ? <img src={sponsor.logo_url} alt={sponsor.sponsor_name} className="h-10 max-w-[160px] object-contain sm:h-12" />
          : <span className="text-base font-extrabold text-[var(--navy)]">{sponsor.sponsor_name}</span>}
      </>
    );
    const cls = `flex items-center gap-3 rounded-xl border border-[var(--paper-border)] bg-white px-4 py-2 shadow-sm ${className}`;
    return sponsor.link
      ? <a href={sponsor.link} target="_blank" rel="noreferrer" data-testid={`section-sponsor-${section}`} className={`${cls} transition-shadow hover:shadow-md`}>{content}</a>
      : <span data-testid={`section-sponsor-${section}`} className={cls}>{content}</span>;
  }

  const inner = (
    <>
      <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">Sponsored by</span>
      {sponsor.logo_url
        ? <img src={sponsor.logo_url} alt={sponsor.sponsor_name} className="h-7 max-w-[140px] object-contain" />
        : <span className="text-sm font-bold text-slate-700">{sponsor.sponsor_name}</span>}
    </>
  );
  const cls = `inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm ${className}`;
  if (sponsor.link) {
    return (
      <a href={sponsor.link} target="_blank" rel="noreferrer" data-testid={`section-sponsor-${section}`} className={`${cls} transition-shadow hover:shadow-md`}>
        {inner}
      </a>
    );
  }
  return <span data-testid={`section-sponsor-${section}`} className={cls}>{inner}</span>;
}
